// Две кнопки в левом нижнем углу и два попапа за ними.
//
// Кнопка i открывает таблицу выплат, кнопка со знаком вопроса — правила игры, см. ui/paytable.ts
// и ui/rules.ts. Попап на экране всегда один: открытие второго закрывает первый.
//
// Кнопки стоят в углу макета, а не экрана, как и кнопка спина: на широком экране поля по краям
// остаются пустыми, и кнопки не уезжают от машины.
//
// Суммы таблицы зависят от ставки, а ставку игрок двигает между раундами. Поэтому ставка
// в таблицу ставится на открытии попапа, а не по каждому шагу кошелька: раунд меняет счёт
// каждый раз, и переписывать таблицу на это незачем.

import { Container } from 'pixi.js';

import type { GameConfig } from '../api/gameConfig';
import { UI_SHEET, type UiFrame } from '../assets/catalog';
import type { AssetManager, TextureSheet } from '../assets/manager';
import type { SymbolLibrary } from '../game/symbols';
import type { Wallet } from '../game/wallet';
import { text } from '../i18n/text';

import { RoundButton } from './button';
import { minTouchableScale, ROW_BUTTON_GAP, rowButtonGap, type GameLayout, viewportEdge } from './layout';
import { Paytable } from './paytable';
import { Popup } from './popup';
import { refreshRulesPages, rulesPages } from './rules';
import { DESIGN_HEIGHT, DESIGN_WIDTH, EDGE_MARGIN } from './scene';

/** Кнопки помощи и попапы за ними. */
export class Help {
  readonly view: Container = new Container();

  readonly #paytable: Paytable;
  readonly #paytablePopup: Popup;
  readonly #rulesPopup: Popup;
  readonly #rulesPopupPages: readonly Container[];

  readonly #info: RoundButton;
  readonly #rules: RoundButton;

  readonly #wallet: Wallet;
  readonly #onOpen: () => void;

  constructor(config: GameConfig, symbols: SymbolLibrary, assets: AssetManager, wallet: Wallet, onOpen: () => void = () => {}) {
    const ui: TextureSheet<UiFrame> = assets.sheet(UI_SHEET);

    this.#wallet = wallet;
    this.#onOpen = onOpen;

    this.#paytable = new Paytable(config, symbols, assets, wallet.stake);
    this.#paytablePopup = new Popup(assets, text('paytable'), this.#paytable.pages);
    this.#rulesPopupPages = rulesPages(assets);
    this.#rulesPopup = new Popup(assets, text('gameRules'), this.#rulesPopupPages);

    this.#info = new RoundButton({ idle: ui.infoIdle, hover: ui.infoHover, active: ui.infoActive }, () => this.#open(this.#paytablePopup));
    this.#rules = new RoundButton({ idle: ui.helpIdle, hover: ui.helpHover, active: ui.helpActive }, () => this.#open(this.#rulesPopup));

    const x: number = -DESIGN_WIDTH / 2 + EDGE_MARGIN + this.#info.radius;
    const y: number = DESIGN_HEIGHT / 2 - EDGE_MARGIN - this.#rules.radius;

    this.#info.view.position.set(x, y);
    this.#rules.view.position.set(x + this.#info.radius + ROW_BUTTON_GAP + this.#rules.radius, y);

    this.view.addChild(this.#info.view, this.#rules.view);
  }

  /**
   * Узлы попапов. Их кладёт в свой слой сцена, см. ADR-54.
   *
   * Затемнение накрывает всё, что лежит под ним, поэтому попапы живут выше всех кнопок игры,
   * а не рядом со своими. В узле помощи они накрыли бы только кнопки помощи.
   */
  get popups(): readonly Container[] {
    return [this.#paytablePopup.view, this.#rulesPopup.view];
  }

  /** Двигает затухание кнопок и содержимое попапов. */
  update(seconds: number): void {
    this.#info.update(seconds);
    this.#rules.update(seconds);
    this.#paytablePopup.update(seconds);
    this.#rulesPopup.update(seconds);
  }

  /** Растягивает затемнение попапов на всю видимую область. */
  layout(layout: GameLayout): void {
    const { bottom, bounds, composition, viewport } = layout;
    const mobile: boolean = composition === 'mobile';
    const infoScale: number = mobile ? minTouchableScale(viewport, this.#info.radius, 24) : 1;
    const rulesScale: number = mobile ? minTouchableScale(viewport, this.#rules.radius, 24) : 1;
    const gap: number = rowButtonGap(layout);
    const edge: number = mobile ? viewportEdge(viewport) : EDGE_MARGIN;
    const left: number = bounds.left + edge;
    const infoX: number = left + this.#info.radius * infoScale;
    const rulesX: number = infoX + this.#info.radius * infoScale + gap + this.#rules.radius * rulesScale;
    const infoY: number = mobile ? bottom.buttonBottom - this.#info.radius * infoScale : bounds.bottom - EDGE_MARGIN - this.#info.radius;
    const rulesY: number = mobile ? bottom.buttonBottom - this.#rules.radius * rulesScale : bounds.bottom - EDGE_MARGIN - this.#rules.radius;

    this.#info.view.scale.set(infoScale);
    this.#rules.view.scale.set(rulesScale);
    this.#info.view.position.set(infoX, infoY);
    this.#rules.view.position.set(rulesX, rulesY);

    this.#paytablePopup.layout(viewport);
    this.#rulesPopup.layout(viewport);
  }

  /** Снимает клавиатуру попапов. Зовётся, когда сцена уходит с экрана. */
  destroy(): void {
    this.#paytablePopup.destroy();
    this.#rulesPopup.destroy();
  }

  /** Закрывает оба окна: настройки и справка не должны затемнять игру одновременно. */
  close(): void {
    this.#paytablePopup.close();
    this.#rulesPopup.close();
  }

  /** Переписывает локализованные подписи, не закрывая и не пересобирая справку. */
  refreshText(): void {
    this.#paytablePopup.title = text('paytable');
    this.#rulesPopup.title = text('gameRules');
    this.#paytable.refreshText();
    refreshRulesPages(this.#rulesPopupPages);
  }

  /** Открывает попап вместо того, который сейчас на экране. */
  #open(popup: Popup): void {
    this.#onOpen();
    this.close();

    // Ставку игрок мог сдвинуть с прошлого открытия, а суммы таблицы обещаны под неё.
    this.#paytable.stake = this.#wallet.stake;

    popup.show();
  }
}
